// CMS-less data layer.
//
// The site no longer talks to Strapi/GraphQL. Page content lives as static JSON
// in /content (extracted from the original CMS via scripts/extract_content.py).
// `queryCMS` keeps the exact same call signature the page containers already use
// — it inspects the GraphQL document's root field to decide which content file
// to return — so the containers' getStaticProps code is untouched.
//
// This runs at build time only (inside getStaticProps/getStaticPaths), so it is
// free to use fs and is stripped from the client bundle by Next.js.
import fs from 'fs';
import path from 'path';

const CONTENT_DIR = path.join(process.cwd(), 'content');

// GraphQL root page field -> content file (relative to /content)
const FIELD_TO_FILE: Record<string, string> = {
  indexPage: 'index.json',
  aboutPage: 'about.json',
  servicesPage: 'services.json',
  processPage: 'process.json',
  contactPage: 'contact.json',
  privacyPage: 'privacy.json',
  notFoundPage: 'not-found.json',
};

const loadRaw = (relPath: string) =>
  JSON.parse(fs.readFileSync(path.join(CONTENT_DIR, relPath), 'utf-8'));

// content/_shared.json is the SINGLE source of truth for header, footer and
// sharedCopy (so the office locations etc. can never drift between pages).
let sharedCache: any = null;
const shared = () => {
  if (!sharedCache) {
    try {
      sharedCache = loadRaw('_shared.json');
    } catch {
      sharedCache = {};
    }
  }
  return sharedCache;
};

// Load a page file, then overlay the shared blocks so every page uses the same
// header/footer/sharedCopy. Unconditional: _shared.json is the single source
// of truth for these — page files no longer embed their own copies (they used
// to, and the duplicates drifted, e.g. stale footer contacts).
const load = (relPath: string) => {
  const data = loadRaw(relPath);
  const s = shared();
  if (s.header) data.header = s.header;
  if (s.footer) data.footer = s.footer;
  if (s.sharedCopy) data.sharedCopy = s.sharedCopy;
  return data;
};

const rootSelections = (query: any): any[] => {
  try {
    const def = (query?.definitions || []).find(
      (d: any) => d.kind === 'OperationDefinition'
    );
    return def?.selectionSet?.selections || [];
  } catch {
    return [];
  }
};

// Resolve which page a GraphQL document is asking for, then return the matching
// pre-normalized static content (same shape Strapi + normalize() used to return).
export const queryCMS = async (params: { query: any }) => {
  const selections = rootSelections(params.query);
  const names = selections.map((s: any) => s?.name?.value).filter(Boolean);

  // case study: caseStudy(id: N)
  const caseSel = selections.find((s: any) => s?.name?.value === 'caseStudy');
  if (caseSel) {
    const idArg = (caseSel.arguments || []).find(
      (a: any) => a?.name?.value === 'id'
    );
    const id = idArg?.value?.value ?? idArg?.value?.fields?.[0]?.value?.value;
    if (id === null || id === undefined)
      throw new Error('queryCMS: caseStudy query missing id');
    return load(path.join('case-studies', `${id}.json`));
  }

  const pageField = names.find((n: string) => FIELD_TO_FILE[n]);
  if (!pageField) {
    throw new Error(
      `queryCMS: no static content mapping for query fields [${names.join(
        ', '
      )}]`
    );
  }
  return load(FIELD_TO_FILE[pageField]);
};

// Returns the list of case study ids available as static content
// (used by getStaticPaths for /case-study/[id]).
export const getCaseStudyIds = (): string[] => {
  try {
    const idx = JSON.parse(
      fs.readFileSync(
        path.join(CONTENT_DIR, 'case-studies-index.json'),
        'utf-8'
      )
    );
    return idx.map((c: any) => String(c.id));
  } catch {
    return [];
  }
};

// Locale list is fixed now that the CMS is gone.
export const getLocales = async () => [{ code: 'en' }];
